import { useMemo } from 'react';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { useQuery } from 'react-query';
import { QualificationType } from '@scale/shared-types';
import {
  QualificationEntryStatus,
  QualificationListStatus,
  QualificationJSON,
  ALWAYS_SHOWN_QUAL_TYPES,
} from '@scale/shared/types/qualification/types';
import { IUserJson } from '@scale/shared/types/user';
import _ from 'lodash';
import { useUnleashFeatureFlagWithLoading } from 'react-scale/components/featureFlag/useUnleashFeatureFlag';
import {
  useGetPeekQueue,
  selectQualificationList,
} from '@scale/outlier-components/hooks/useGetPeekQueue';

const todoQualificationStatus = [
  QualificationEntryStatus.Unstarted,
  QualificationEntryStatus.WorkerPending,
  QualificationEntryStatus.CorpPending,
];

const useProjectsTodo = (user: IUserJson, findPending?: boolean) => {
  const { data: usePeekQueue, isSuccess: loadedCanUsePeekQueueFF } =
    useUnleashFeatureFlagWithLoading('use-peek-queue', {}, false);

  const { data: peekQueue, isLoading: isLoadingPeekQueue } = useGetPeekQueue({
    enabled: loadedCanUsePeekQueueFF && (usePeekQueue ?? false),
  });

  const { data: qualificationInfoFromLegacyEndpoint, isLoading: isLoadingQualInfo } = useQuery(
    ['internal/experts/qualification/info', user?.id ?? '', findPending],
    async () =>
      apiFetch(null, 'internal/experts/qualification/info', {
        method: 'GET',
        query: { findPending },
      }),
    {
      refetchOnWindowFocus: false,
      // consider data fresh for 1 minute, to avoid unnecessary refetches
      staleTime: 60 * 1000,
      // some callers of this hook ignore type so user might be null or undefined
      enabled: !!user && !usePeekQueue && loadedCanUsePeekQueueFF,
    },
  );

  const isLoading =
    !loadedCanUsePeekQueueFF || (usePeekQueue ? isLoadingPeekQueue : isLoadingQualInfo);

  const qualificationInfo = useMemo(
    () => (usePeekQueue ? selectQualificationList(peekQueue) : qualificationInfoFromLegacyEndpoint),
    [usePeekQueue, peekQueue, qualificationInfoFromLegacyEndpoint],
  );

  const data = useMemo(() => {
    if (isLoading) {
      return {
        data: {},
        isLoading: true,
      };
    }
    if (!qualificationInfo) {
      return {
        data: {},
        isLoading: false,
      };
    }
    let {
      requiredQualificationListInfo,
      qualificationListStatus,
      qualificationListTag,
      qualificationListId,
      requiredQualificationListProjectDescription,
      requiredQualificationListProjectId,
      requiredQualificationListProjectName,
      requiredQualificationListProjectDisplayName,
      teamDisplayName: teamName,
      teamDescription,
      projectId,
      team: activeWorkerTeamName,
      firstDirectAssignment,
      reviewLevel,
    } = qualificationInfo;

    const queryParams = new URLSearchParams(window?.location?.search);
    const testStatus = queryParams.get('testStatus');
    const testQualif = queryParams.get('testQualif');

    if (testQualif) {
      const qualifStatus = testQualif.split(',');
      requiredQualificationListInfo.forEach((qualification: any, i: number) => {
        const testValue = qualifStatus[i];
        switch (testValue) {
          case '0':
            qualification.qualificationStatus = QualificationEntryStatus.Unstarted;
            break;
          case '1':
            qualification.qualificationStatus = QualificationEntryStatus.WorkerPending;
            break;
          case '2':
            qualification.qualificationStatus = QualificationEntryStatus.CorpPending;
            break;
          case '3':
            qualification.qualificationStatus = QualificationEntryStatus.Qualified;
            break;
          case '4':
            qualification.qualificationStatus = QualificationEntryStatus.Failed;
            break;
          default:
            break;
        }
      });
    }

    switch (testStatus) {
      case QualificationListStatus.Qualified:
        // Scenario 1: Is Qualified
        qualificationListStatus = QualificationListStatus.Qualified;
        break;
      case QualificationListStatus.Failed:
        // Scenario 2: Is Failed
        qualificationListStatus = QualificationListStatus.Failed;
        break;
      case QualificationListStatus.Pending:
        // Scenario 3: Is In Progress
        qualificationListStatus = QualificationListStatus.Pending;
        break;
      default:
        break;
    }

    const canTask =
      !requiredQualificationListInfo.length ||
      (requiredQualificationListInfo.length &&
        (qualificationListStatus === QualificationListStatus.Qualified || user?.isRemoAdmin));

    const [unstartedQualifications, completedQualificationsWithWorkerSkills] = _.partition(
      requiredQualificationListInfo,
      (r: QualificationJSON) =>
        todoQualificationStatus.includes(r.qualificationStatus!) ||
        ALWAYS_SHOWN_QUAL_TYPES.includes(r.qualificationType) ||
        r.metadata.isAlwaysVisible, // WFE Project with access links are never completed
    );

    const completedQualifications = completedQualificationsWithWorkerSkills.filter(
      (r: QualificationJSON) =>
        r.qualificationType !== QualificationType.WorkerSkill &&
        r.qualificationType !== QualificationType.Experiment &&
        r.qualificationType !== QualificationType.Tag,
    );

    const failedQualifications = requiredQualificationListInfo.filter(
      (r: QualificationJSON) => r.qualificationStatus === QualificationEntryStatus.Failed,
    );
    return {
      teamName,
      teamDescription,
      unstartedQualifications,
      completedQualifications,
      failedQualifications,
      canTask,
      qualificationListStatus,
      requiredQualificationListProjectId,
      requiredQualificationListProjectName,
      requiredQualificationListProjectDisplayName,
      requiredQualificationListProjectDescription,
      qualificationListTag,
      qualificationListId,
      projectId,
      activeWorkerTeamName,
      firstDirectAssignment,
      reviewLevel,
      isEQ: peekQueue?.isEmptyQueue,
    };
  }, [isLoading, qualificationInfo, user?.isRemoAdmin, peekQueue]);

  return {
    data,
    isLoading,
  };
};

export default useProjectsTodo;
